import { createApi } from "@reduxjs/toolkit/query/react";
import { endpoints } from "consts";
import { generateRequestUrl } from "helpers/dataHelpers";
import {
  GetLoyaltyCustomersRequestDto,
  GetLoyaltyCustomersResponseDto,
  Loyalty,
  LoyaltyLogs,
  LoyaltySetting,
  UpdateLoyaltyRequestDto,
} from "types/loyalty";

import { coreQuery } from "utils/baseQuery";
import { loyaltyTiersMapper, loyaltyTiersPayloadMapper } from "utils/mappers";

export const loyaltyAPI = createApi({
  reducerPath: "loyaltyAPI",
  baseQuery: coreQuery(),
  tagTypes: ["loyalty", "loyaltySettings"],
  endpoints: build => ({
    getLoyalty: build.query<Loyalty[], void>({
      query: () => ({
        url: endpoints.loyalty,
        method: "GET",
      }),
      transformResponse: (response: Loyalty[]) => loyaltyTiersMapper(response),
      providesTags: ["loyalty"],
    }),
    getLoyaltyCustomers: build.query<
      GetLoyaltyCustomersResponseDto,
      GetLoyaltyCustomersRequestDto
    >({
      query: ({ id, query }) => ({
        url: generateRequestUrl(`${endpoints.loyalty}/${id}/customers`, query),
        method: "GET",
      }),
      providesTags: ["loyalty"],
    }),
    updateLoyalty: build.mutation<void, UpdateLoyaltyRequestDto>({
      query: ({ id, ...data }) => ({
        url: `${endpoints.loyalty}/${id}`,
        method: "PUT",
        data: loyaltyTiersPayloadMapper(data),
      }),
      invalidatesTags: ["loyalty"],
    }),
    getLoyaltySettings: build.query<LoyaltySetting, void>({
      query: () => ({
        url: `${endpoints.loyalty}/settings`,
        method: "GET",
      }),
      providesTags: ["loyaltySettings"],
    }),
    updateLoyaltySettings: build.mutation<LoyaltySetting, LoyaltySetting>({
      query: data => ({
        url: `${endpoints.loyalty}/settings`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["loyaltySettings"],
    }),
    getLoyaltyCustomerLogs: build.query<LoyaltyLogs, { id: string }>({
      query: ({ id }) => ({
        url: `${endpoints.loyalty}/${id}/logs`,
        method: "GET",
      }),
      providesTags: ["loyalty"],
    }),
  }),
});

export const {
  useGetLoyaltyQuery,
  useUpdateLoyaltyMutation,
  useGetLoyaltySettingsQuery,
  useUpdateLoyaltySettingsMutation,
  useGetLoyaltyCustomersQuery,
  useGetLoyaltyCustomerLogsQuery,
  useLazyGetLoyaltyCustomersQuery,
} = loyaltyAPI;
